import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormElement } from '../FormElement';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputText } from '../Inputs/InputText';
import { InputPhone } from '../Inputs/InputPhone';
import Button from '../core/Button';
import { useCallback, useState } from 'react';
import {
  createCollectionMemberBody,
  getGetCollectionsForDepartmentQueryKey,
  getGetTravellersForDepartmentQueryKey,
  getGetTravellersPerOrganizationQueryKey,
  useCreateCollectionMember,
  useGetCollectionsForDepartment,
  useGetDepartmentByID,
  useGetDepartmentsList,
  useGetTravellersPerOrganizationHook,
  useLinkTravellerBobcatAccount,
} from '@/api/trafikportalen';
import { Loading } from '../Loading';
import { toast } from '@/hooks/useToast';
import { DepartmentPicker } from '../DepartmentPicker';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { FormSubmitActions } from '../FormSubmitActions';
import { useMemo } from 'react';
import { trimData } from '@/helpers/trimData';
import { formatPersonnummer } from '@/helpers/formatting';
import { SelectedDepartment } from '@/types/selectedDepartment';
import { getWriteAccessDepartmentIds } from '@/helpers/departmentPermissions';
import { useTravellerDuplicateDetection } from '@/hooks/useTravellerDuplicateDetection';
import { DuplicateMatchesList } from './DuplicateMatchesList';
import type { DuplicateMatch } from '@/hooks/useTravellerDuplicateDetection';
import { MOBILE_PHONE_REGEX } from '@/helpers/validation';
import { cn } from '@/helpers/classnames';
import { findCreatedTraveller } from '@/helpers/createdTraveller';

const schema = createCollectionMemberBody.and(
  z.object({
    phone: z
      .string()
      .regex(MOBILE_PHONE_REGEX, {
        message: 'Telefonnummer måste vara + följt av exakt 11 siffror',
      })
      .optional()
      .or(z.literal('')),
    email: z.string().email(),
    bobcat_private_email: z.string().email().optional().or(z.literal('')),
  }),
);

type TravellerFormType = z.infer<typeof schema>;

interface ProfileCreateFormProps {
  departmentId?: string;
  collectionId?: string;
  onOpenChange: (open: boolean) => void;
  canSelectDepartment?: boolean;
}

type FormView = 'details' | 'departmentPicker';

export const ProfileCreateForm = ({
  departmentId,
  collectionId,
  onOpenChange,
  canSelectDepartment = true,
}: ProfileCreateFormProps) => {
  const customErrorMap = useCustomErrorMap;
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { data: allDepartments } = useGetDepartmentsList();

  const createCollectionMember = useCreateCollectionMember();
  const getTravellersPerOrganization = useGetTravellersPerOrganizationHook();
  const linkBobcatAccount = useLinkTravellerBobcatAccount();
  const { data: department, isLoading } = useGetDepartmentByID(departmentId!);

  const [selectedDepartment, setSelectedDepartment] =
    useState<SelectedDepartment | null>(null);

  const [currentView, setCurrentView] = useState<FormView>('details');
  const [showDuplicates, setShowDuplicates] = useState(true);

  const customResolver = zodResolver(schema, {
    errorMap: customErrorMap(),
  });

  const methods = useForm<TravellerFormType>({
    defaultValues: {
      half_product: false,
      collection_id: '00000000-0000-0000-0000-000000000000',
      email: '',
    },
    resolver: async (values, context, options) => {
      const processedValues = { ...values };
      if (processedValues.phone === '') {
        processedValues.phone = undefined;
      }
      if (processedValues.national_identity_number === '') {
        processedValues.national_identity_number = undefined;
      }
      if (processedValues.bobcat_private_email === '') {
        processedValues.bobcat_private_email = undefined;
      }
      return customResolver(processedValues, context, options);
    },
    mode: 'onBlur',
  });

  const watchedValues = methods.watch();

  const { duplicates } = useTravellerDuplicateDetection({
    email: watchedValues.email,
    first_name: watchedValues.first_name,
    last_name: watchedValues.last_name,
    national_identity_number: watchedValues.national_identity_number,
    phone: watchedValues.phone,
  });

  const cancelEdit = useCallback(() => {
    onOpenChange(false);
    methods.reset();
  }, [methods, onOpenChange]);

  const activeDepartment = useMemo(() => {
    if (selectedDepartment) {
      return selectedDepartment;
    }
    if (department) {
      return department;
    }
    return allDepartments?.find((department) =>
      getWriteAccessDepartmentIds().includes(department.id),
    );
  }, [selectedDepartment, department, allDepartments]);

  const alreadyInDepartment = useMemo(() => {
    return duplicates
      .filter((d) => d.matchedFields.includes('email'))
      .some((d) =>
        d.traveller.memberships?.some(
          (m) => m.department_id === activeDepartment?.id,
        ),
      );
  }, [duplicates, activeDepartment?.id]);

  const { data: collections } = useGetCollectionsForDepartment(
    activeDepartment?.id || '',
  );

  const onSubmit: SubmitHandler<TravellerFormType> = useCallback(
    (data) => {
      const submitData = JSON.parse(JSON.stringify(trimData(data)));

      [
        'first_name',
        'last_name',
        'email',
        'phone',
        'national_identity_number',
        'bobcat_private_email',
      ].forEach((field) => {
        if (submitData[field] === '') {
          submitData[field] = undefined;
        }
      });

      const bobcatPrivateEmail = submitData.bobcat_private_email?.toLowerCase();
      delete submitData.bobcat_private_email;

      const submittedEmail = submitData.email?.toLowerCase();

      createCollectionMember.mutate(
        {
          data: {
            ...submitData,
            collection_id:
              collectionId ||
              collections?.find((c) => c.type === 'master')?.id ||
              '',
          },
        },
        {
          onSuccess: async () => {
            toast.success(
              { title: formatMessage({ defaultMessage: 'Resenär skapad.' }) },
              { autoClose: 3000 },
            );

            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: getGetTravellersPerOrganizationQueryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: getGetCollectionsForDepartmentQueryKey(
                  activeDepartment?.id ?? '',
                ),
              }),
              queryClient.invalidateQueries({
                queryKey: getGetTravellersForDepartmentQueryKey(
                  activeDepartment?.id ?? '',
                ),
              }),
            ]);

            if (bobcatPrivateEmail && submittedEmail) {
              try {
                const travellers = await queryClient.fetchQuery({
                  queryKey: getGetTravellersPerOrganizationQueryKey(),
                  queryFn: ({ signal }) => getTravellersPerOrganization(signal),
                });
                const createdTraveller = findCreatedTraveller(
                  travellers ?? [],
                  {
                    email: submittedEmail,
                    firstName: submitData.first_name,
                    lastName: submitData.last_name,
                  },
                );
                if (createdTraveller) {
                  await linkBobcatAccount.mutateAsync({
                    travellerID: createdTraveller.traveller.id,
                    data: { bobcat_private_email: bobcatPrivateEmail },
                  });
                  await queryClient.invalidateQueries({
                    queryKey: getGetTravellersPerOrganizationQueryKey(),
                  });
                } else {
                  throw new Error('Created traveller not found');
                }
              } catch (error) {
                console.error('Failed to link bobcat account:', error);
                toast.error(
                  {
                    title: formatMessage({
                      defaultMessage:
                        'Kunde inte koppla E-post i appen / Mina sidor. Försök igen från resenärens profil.',
                    }),
                  },
                  { autoClose: 4000 },
                );
              }
            }

            onOpenChange(false);
          },
          onError: (error) => {
            console.error('Error: ', error);
            toast.error(
              {
                title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
                description: formatMessage({
                  defaultMessage:
                    'Försök spara igen senare, eller vänligen kontakta support.',
                }),
              },
              { autoClose: 3000 },
            );
            return;
          },
        },
      );
    },
    [
      createCollectionMember,
      collectionId,
      collections,
      activeDepartment?.id,
      onOpenChange,
      queryClient,
      formatMessage,
      getTravellersPerOrganization,
      linkBobcatAccount,
    ],
  );

  const handleDepartmentSelect = useCallback(
    (department: SelectedDepartment) => {
      setSelectedDepartment(department);
      setCurrentView('details');
    },
    [],
  );

  const handleDismissDuplicates = useCallback(() => {
    setShowDuplicates(false);
  }, []);

  const handleSelectMatch = useCallback(
    (match: DuplicateMatch) => {
      const { email, first_name, last_name, national_identity_number } =
        match.traveller.traveller;
      onSubmit({
        collection_id: '00000000-0000-0000-0000-000000000000',
        email: email || '',
        first_name: first_name || '',
        last_name: last_name || '',
        national_identity_number: national_identity_number || '',
        half_product: false,
      });
    },
    [onSubmit],
  );

  const renderFormContent = useCallback(() => {
    switch (currentView) {
      case 'departmentPicker':
        return (
          <div className="flex flex-col items-center gap-2">
            <DepartmentPicker
              title={<FormattedMessage defaultMessage="Välj avdelning" />}
              suggestedDepartmentId={activeDepartment?.id}
              onSelect={handleDepartmentSelect}
              enabledDepartments={getWriteAccessDepartmentIds()}
            />
            <Button variant="ghost" onClick={() => setCurrentView('details')}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
          </div>
        );

      case 'details':
      default:
        return (
          <div className="flex flex-col gap-2">
            <FormElement
              id="email"
              label={<FormattedMessage defaultMessage="E-post" />}
              required={true}
              input={
                <InputText
                  name="email"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.email}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="first_name"
              label={<FormattedMessage defaultMessage="Förnamn" />}
              input={
                <InputText
                  name="first_name"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.first_name}
                />
              }
            />
            <FormElement
              id="last_name"
              label={<FormattedMessage defaultMessage="Efternamn" />}
              input={
                <InputText
                  name="last_name"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.last_name}
                />
              }
            />
            <FormElement
              id="national_identity_number"
              label={<FormattedMessage defaultMessage="Personnummer" />}
              input={
                <InputText
                  name="national_identity_number"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.national_identity_number}
                  placeholder={formatMessage({
                    defaultMessage: 'YYYYMMDD-XXXX',
                  })}
                  onBlur={() => {
                    if (!watchedValues.national_identity_number) return;
                    const formatted = formatPersonnummer(
                      watchedValues.national_identity_number,
                    );
                    methods.setValue('national_identity_number', formatted, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="bobcat_private_email"
              label={
                <FormattedMessage defaultMessage="E-post i appen / Mina sidor" />
              }
              input={
                <InputText
                  name="bobcat_private_email"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.bobcat_private_email}
                  helperText={formatMessage({
                    defaultMessage:
                      'Används för att skicka biljetter till resenärens konto i resenärsappen. Får inte innehålla å, ä eller ö.',
                  })}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="phone"
              label={<FormattedMessage defaultMessage="Mobilnummer" />}
              input={
                <InputPhone
                  name="phone"
                  control={methods.control}
                  editMode={true}
                  error={methods.formState.errors.phone}
                  helperText={formatMessage({
                    defaultMessage:
                      'Används enbart för att skicka SMS om tjänsteresa i app. Separat process krävs för att utskick ska göras.',
                  })}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Avdelning" />}
              button={
                canSelectDepartment ? (
                  <Button
                    className={cn(
                      'w-full',
                      activeDepartment?.name &&
                        'justify-start truncate text-left',
                    )}
                    onClick={() => setCurrentView('departmentPicker')}
                  >
                    <p className="truncate">
                      {activeDepartment?.name ?? 'Välj en avdelning'}
                    </p>
                  </Button>
                ) : (
                  department?.name
                )
              }
              input={null}
            />

            {showDuplicates && (
              <DuplicateMatchesList
                matches={duplicates}
                departmentId={activeDepartment?.id || ''}
                onDismiss={handleDismissDuplicates}
                onSelectMatch={handleSelectMatch}
              />
            )}
          </div>
        );
    }
  }, [
    currentView,
    activeDepartment?.id,
    activeDepartment?.name,
    handleDepartmentSelect,
    methods,
    formatMessage,
    canSelectDepartment,
    department?.name,
    showDuplicates,
    duplicates,
    handleDismissDuplicates,
    handleSelectMatch,
    watchedValues.national_identity_number,
  ]);

  if (isLoading) return <Loading />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">{renderFormContent()}</div>
        {currentView === 'details' && (
          <FormSubmitActions
            active={true}
            createMode={true}
            onCancel={cancelEdit}
            disabled={!activeDepartment?.id || alreadyInDepartment}
            isLoading={createCollectionMember.isPending}
          />
        )}
      </form>
    </FormProvider>
  );
};
